/**
 * ai-model-picker.tsx — Ô chọn model AI DÙNG CHUNG (Nguồn AI và lượt AI, màn nhân viên AI…).
 *
 * Tự tải danh mục `/ai/models`, nguồn `/ai/sources`, ví `/ai/credits` (TanStack Query dùng chung cache, không cần truyền).
 * Mỗi nền tảng ghi nguồn (AI của eCDP / Key của tôi); mỗi model ghi credit/lượt hoặc "Key của tôi"; model bị khoá theo
 * gói hoặc theo nguồn hiện 🔒 + lý do và không chọn được. Dưới ô: dòng nguồn + chi phí / cảnh báo (tắt bằng `showInfo={false}`).
 *
 * Server chưa có `/ai/models` → dùng danh sách nền tảng của `/ai/config` (không khoá được, ghi chú rõ).
 *
 * Props:
 *  - `value`: `{ provider, model }` đang chọn (null / thiếu = chưa chọn).
 *  - `onChange(v)`: model mới; `null` chỉ xảy ra khi `allowInherit` và chọn dòng "dùng mặc định".
 *  - `allowInherit` (mặc định false): thêm dòng đầu "— Dùng mặc định —" (`inheritLabel` đổi chữ).
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LegacyProviderWarning } from '@/components/shared/ai-provider-option'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { isLegacyAiProvider, supportedProvidersOf } from '@/lib/ai-providers'
import type { SourceInfo } from '@/lib/ai-credits'
import { useAiConfig } from '@/hooks/use-ai'
import { useAiCredits, useAiModels, useAiSources, useCanBuyAiCredits } from '@/hooks/use-ai-credits'
import { BuyCreditsDialog } from '@/pages/ai/credits/buy-credits-dialog'
import {
  buildPickerGroups, decodePickerValue, encodePickerValue, findPickerOption, pickerInfo, type PickerValue,
} from './ai-model-picker-lib'

export type { PickerValue } from './ai-model-picker-lib'

const TONE_CLASS: Record<SourceInfo['tone'], string> = {
  info: 'border-primary/20 bg-primary/5',
  ok: 'border-success/30 bg-success/5',
  warn: 'border-warning/40 bg-warning/10',
  danger: 'border-destructive/30 bg-destructive/5',
}

export interface AiModelPickerProps {
  value: PickerValue | null
  onChange: (v: PickerValue | null) => void
  /** Cho chọn "dùng mặc định" (giá trị null). Mặc định: không. */
  allowInherit?: boolean
  inheritLabel?: string
  disabled?: boolean
  ariaLabel?: string
  /** Hiện dòng nguồn + chi phí / cảnh báo dưới ô (mặc định có). */
  showInfo?: boolean
  className?: string
}

export function AiModelPicker({
  value, onChange, allowInherit = false, inheritLabel, disabled, ariaLabel: ariaLabelProp, showInfo = true, className,
}: AiModelPickerProps) {
  const { t } = useTranslation('aiComp')
  const ariaLabel = ariaLabelProp ?? t('modelPicker.aria')
  const { data: modelsData } = useAiModels()
  const { data: sources } = useAiSources()
  const { data: credits } = useAiCredits()
  const canBuy = useCanBuyAiCredits()
  const { data: config } = useAiConfig()
  const [buyOpen, setBuyOpen] = useState(false)

  const catalog = modelsData?.models ?? null
  const groups = useMemo(() => (catalog ? buildPickerGroups(catalog, sources?.providers) : []), [catalog, sources])
  const encoded = encodePickerValue(value)
  const known = !encoded || (catalog ? !!findPickerOption(groups, value) : true)
  const legacy = isLegacyAiProvider(value?.provider)
  const info = showInfo ? pickerInfo({ value, catalog, sources: sources?.providers, credits }) : null

  const selectCls = 'w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring disabled:opacity-60 dark:bg-card'
  const handle = (s: string) => onChange(s ? decodePickerValue(s) : null)
  // Chưa chọn và không cho "mặc định" → dòng gợi ý (không chọn được) để ô không tự hiện model đầu tiên.
  const placeholder = !encoded && !allowInherit

  return (
    <div className={cn('space-y-1.5', className)}>
      {catalog ? (
        <select className={selectCls} value={encoded} onChange={(e) => handle(e.target.value)} disabled={disabled} aria-label={ariaLabel}>
          {allowInherit && <option value="">{inheritLabel ?? t('modelPicker.useDefault')}</option>}
          {placeholder && <option value="" disabled>{t('modelPicker.placeholder')}</option>}
          {!known && <option value={encoded}>{value?.model} {t('modelPicker.notInCatalog')}</option>}
          {groups.map((g) => (
            <optgroup key={g.provider} label={g.label}>
              {g.options.map((o) => (
                <option key={o.value} value={o.value} disabled={o.disabled} title={o.lockReason ?? undefined}>{o.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
      ) : (
        // Server chưa có danh mục /ai/models: danh sách nền tảng của /ai/config, không khoá được theo gói.
        <select className={selectCls} value={encoded} onChange={(e) => handle(e.target.value)} disabled={disabled} aria-label={ariaLabel}>
          {allowInherit && <option value="">{inheritLabel ?? t('modelPicker.useDefault')}</option>}
          {placeholder && <option value="" disabled>{t('modelPicker.placeholder')}</option>}
          {encoded && !(config?.availableProviders ?? []).some((p) => p.id === value?.provider && p.models.some((m) => m.value === value?.model)) && (
            <option value={encoded}>{value?.model} {t('modelPicker.notInCatalog')}</option>
          )}
          {supportedProvidersOf(config?.availableProviders ?? []).map((p) => (
            <optgroup key={p.id} label={p.name}>
              {p.models.map((m) => <option key={m.value} value={`${p.id}:${m.value}`}>{m.title}</option>)}
            </optgroup>
          ))}
        </select>
      )}
      {legacy && value?.provider && <LegacyProviderWarning provider={value.provider} />}
      {info && (
        <div className={cn('flex flex-wrap items-center gap-2 rounded-lg border px-3 py-1.5 text-xs', TONE_CLASS[info.tone])}>
          <span className="flex-1">{info.text}</span>
          {info.buy && canBuy && (
            <Button type="button" size="sm" variant={info.tone === 'danger' ? 'default' : 'outline'} className="h-7" onClick={() => setBuyOpen(true)}>
              {t('modelPicker.buyTokens')}
            </Button>
          )}
        </div>
      )}
      {buyOpen && <BuyCreditsDialog open={buyOpen} onOpenChange={setBuyOpen} />}
    </div>
  )
}
